// src/features/estoque/components/DashboardEstoque.jsx
import React, { useState } from "react";
import { useResumoEstoque } from "../hooks/useResumoEstoque";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { DollarSign, ArrowUpCircle, ArrowDownCircle } from "lucide-react";

const DashboardEstoque = () => {
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth());
  const [ano, setAno] = useState(hoje.getFullYear());

  const { dadosGrafico, totais, isLoading } = useResumoEstoque(mes, ano);

  return (
    <Card className="col-span-1 lg:col-span-2">
      <CardHeader>
        <div className="flex flex-col md:flex-row justify-between items-start gap-4">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Resumo Financeiro de Estoque
            </CardTitle>
            <CardDescription>
              Valores contabilizados em Entradas e Saídas via Notas Fiscais.
            </CardDescription>
          </div>

          {/* Filtros de Mês e Ano */}
          <div className="flex gap-3">
            <div className="space-y-1">
              <Label htmlFor="mesFiltro" className="text-xs text-muted-foreground">Mês</Label>
              <select
                id="mesFiltro"
                value={mes}
                onChange={(e) => setMes(Number(e.target.value))}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const data = new Date(2000, i, 1);
                  return (
                    <option key={i} value={i} className="dark:bg-slate-800">
                      {data.toLocaleString("pt-BR", { month: "long" })}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="anoFiltro" className="text-xs text-muted-foreground">Ano</Label>
              <Input
                id="anoFiltro"
                type="number"
                value={ano}
                onChange={(e) => setAno(Number(e.target.value))}
                className="h-9 w-24"
              />
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Totalizadores Supriores */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center justify-between gap-2 overflow-hidden">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-green-800 dark:text-green-300 truncate">Total de Entradas</p>
              <h3 className="text-xl md:text-2xl font-bold text-green-700 dark:text-green-400 truncate" title={totais.entradas.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}>
                {totais.entradas.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
              </h3>
            </div>
            <ArrowDownCircle className="w-8 h-8 text-green-600 dark:text-green-500 opacity-80 flex-shrink-0" />
          </div>

          <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded-lg p-4 flex items-center justify-between gap-2 overflow-hidden">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-orange-800 dark:text-orange-300 truncate">Total de Saídas</p>
              <h3 className="text-xl md:text-2xl font-bold text-orange-700 dark:text-orange-400 truncate" title={totais.saidas.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}>
                {totais.saidas.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
              </h3>
            </div>
            <ArrowUpCircle className="w-8 h-8 text-orange-600 dark:text-orange-500 opacity-80 flex-shrink-0" />
          </div>
        </div>

        {/* Gráfico Recharts */}
        <div className="h-[250px] w-full pt-4">
          {isLoading ? (
            <div className="h-full flex items-center justify-center">
              <p className="text-muted-foreground animate-pulse">
                Carregando gráfico...
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={dadosGrafico}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                barSize={60}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                <XAxis 
                  dataKey="name" 
                  tickLine={false} 
                  axisLine={false} 
                  tickMargin={10} 
                  fontSize={14} 
                  fontWeight="bold"
                />
                <YAxis
                  allowDecimals={false}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(valor) => `R$ ${valor / 1000}k`}
                  fontSize={12}
                />
                <RechartsTooltip
                  cursor={{ fill: 'transparent' }}
                  contentStyle={{
                    backgroundColor: "hsl(var(--background))",
                    borderColor: "hsl(var(--border))",
                    borderRadius: "0.5rem",
                    fontSize: "14px",
                  }}
                  formatter={(value) => value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                />
                <Bar dataKey="valor" radius={[4, 4, 0, 0]}>
                  {dadosGrafico.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardEstoque;
